import { useMemo } from 'react'
import { parseAudioNoteUri } from '@highlight/common/product'
import {
  useHybridSearch,
  type HybridSearchResponse,
  type HybridSearchResult,
} from '@renderer/windows/features/context/hooks/use-hybrid-search'

type SearchHighlight = NonNullable<HybridSearchResult['highlights']>[number]

export interface AudioNoteMatch {
  audioNoteId: string
  /** The first matched passage across the note's results, with the query's words marked. */
  highlight: SearchHighlight | null
  /** When the meeting happened, or when the note was made if it has no start. */
  happenedAt: string | null
}

interface AudioNoteSearch {
  matches: Array<AudioNoteMatch>
  /** True until the first response for the current query lands. Later queries keep the previous matches on screen. */
  isLoading: boolean
  error: Error | null
  emptyReason: HybridSearchResponse['reason']
}

/** Searches the signed-in user's audio notes through hybrid search, one match per note. `query` should already be debounced. */
export function useAudioNoteSearch(query: string): AudioNoteSearch {
  const search = useHybridSearch({ query, source: 'audio' })
  const matches = useMemo(() => groupResultsByAudioNote(search.data?.results ?? []), [search.data])

  return {
    matches,
    isLoading: !search.data && !search.error,
    error: search.error,
    emptyReason: search.data?.reason ?? null,
  }
}

/** Hybrid search returns pieces of a note (summary, transcript passages), so one note can appear several times. */
function groupResultsByAudioNote(results: Array<HybridSearchResult>): Array<AudioNoteMatch> {
  const matchesById = new Map<string, AudioNoteMatch>()
  for (const result of results) {
    const audioNoteId = result.canonicalUrl ? parseAudioNoteUri(result.canonicalUrl)?.id : undefined
    if (!audioNoteId) {
      continue
    }
    const highlight = result.highlights?.[0] ?? null
    const existing = matchesById.get(audioNoteId)
    if (existing) {
      existing.highlight ??= highlight
      continue
    }
    matchesById.set(audioNoteId, { audioNoteId, highlight, happenedAt: result.ts ?? result.createdTs })
  }
  return [...matchesById.values()]
}
